import { Component, CUSTOM_ELEMENTS_SCHEMA, computed, inject, OnDestroy, OnInit, signal, effect, untracked } from '@angular/core';
import { ActionTypesEnum } from '../../common/app-config/filter-models/misc';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { Location } from '@angular/common';
import { ApplicationStatusEnum, BriefProgressEnum, BriefResponseDto, BriefStatusEnum, SocialChannelEnum } from '../../common/general-types/briefs.dto';
import { summariseBriefAsk } from '@/app/common/brief-ask/summarise-brief-ask';
import { IconsEnum } from '../../common/general-types/icons.dto';
import { SubmittedPostVersionEnum } from '../../common/general-types/briefs.dto';
import { BriefService } from '../../services/brief/brief.service';
import { Subscription, take } from 'rxjs';
import { FilterStatsDto } from '../../common/general-types/filters.dto';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { AlertService } from '../../atoms/alert/alert.service';
import { ManageBriefTabsService } from './service/manage-brief-tabs/manage-brief-tabs.service';
import { BriefingComponent } from './tabs/briefing/briefing.component';
import { AssignmentComponent } from './tabs/assignment/assignment.component';
// import { ModerationComponent } from './tabs/moderation/moderation.component';
import { ReportingComponent } from './tabs/reporting/reporting.component';
import { MagicSearchService } from '../creators/service/magic-search/magic-search.service';
import { CentralManagementService } from '../../services/central-management/central-management.service';
import { CreatorProfileComponent } from '../creators/molecules/creator-profile/creator-profile.component';
import { PromoteToBriefComponent } from '../manage-brief/molecules/promote-to-brief/promote-to-brief.component';
import { ReviewApplicationModal } from '../manage-brief/molecules/review-application-modal/review-application-modal';
import { ActivityFeedComponent } from './tabs/activity-feed/activity-feed.component';
import { ChoosePlanService } from '../choose-plan/service/choose-plan/choose-plan.service';
import { ChooseAutoPilotComponent } from '../choose-auto-pilot/choose-auto-pilot.component';
import { ReportingTtoComponent } from './tabs/reporting-tto/reporting-tto';
import { BoostTiktokV2Component } from './tabs/boost-tiktok-v2/boost-tiktok-v2';
import { SparkAds } from './tabs/spark-ads/spark-ads';
import { Logistics } from './tabs/logistics/logistics';
import { ApprovedContent } from './tabs/approved-content/approved-content';
import { ApprovedContentService } from './tabs/approved-content/approved-content.service';
import { ToastNotificationService } from '../../atoms/toast-notification/toast-notification.service';
import { ThemeService } from '../../services/theme/theme.service';
import { RetroV2 } from './tabs/retro-v2/retro-v2';
import { HasRoleDirectiveDirective } from "@/app/common/directives/has-role/has-role-directive.directive";
import { BackButton } from '../../atoms/back-button/back-button';
import {
  creatorIdFromManageBriefDetail,
  isManageBriefAssignmentSubTab,
  manageBriefCommands,
  slugForManageBriefTab,
  tabFromManageBriefParam,
} from './manage-brief-tab.routing';
import { briefCarriesAdLicense } from '../../common/brief-pieces/ad-license';
import { showsReportingTab as briefShowsReportingTab, showsTikTokAdsTabs as briefShowsTikTokAdsTabs } from './manage-brief-tab-visibility';

@Component({
  selector: 'app-manage-brief-tabs',
  standalone: true,
  imports: [BriefingComponent, AssignmentComponent,
    //  ModerationComponent, 
    ReportingComponent, ReportingTtoComponent, CreatorProfileComponent,
    PromoteToBriefComponent, ReviewApplicationModal, ActivityFeedComponent,
    BoostTiktokV2Component, SparkAds, ChooseAutoPilotComponent, Logistics, RetroV2, HasRoleDirectiveDirective, RouterLink, ApprovedContent],
  templateUrl: './manage-brief-tabs.component.html',
  styleUrl: './manage-brief-tabs.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ManageBriefTabsComponent implements OnInit, OnDestroy {


  protected readonly ApplicationStatusEnum = ApplicationStatusEnum;
  protected readonly IconsEnum = IconsEnum;
  protected readonly SubmittedPostVersionEnum = SubmittedPostVersionEnum;
  protected readonly BriefProgressEnum = BriefProgressEnum;
  protected readonly SocialChannelEnum = SocialChannelEnum;

  /** layout variables */
  selectedTab = signal<BriefProgressEnum>(BriefProgressEnum.ActivityFeed);
  private readonly approvedContentService = inject(ApprovedContentService);
  /** Approved videos on this brief, for the tab badge (hidden at 0). */
  protected readonly approvedContentCount = computed(() => {
    const state = this.approvedContentService.state();
    return state.status === 'loaded' && state.briefId === this.manageBriefTabsService.focussedBrief()?.id
      ? state.data.videos.length
      : 0;
  });
  selectedSubTab = signal<number>(0);
  /** layout variables - END */

  /** Brief Settings Variables */
  briefSettingsOptions: {
    id: number;
    title: string;
    description: string;
    action: string;
    icon: 'private' | 'public' | 'cancelInvites' | 'declineAll' | 'unhireAll' | 'complete' | 'hireMessage';
  }[] = [
    {
      id: 8,
      title: 'Hire Message',
      description: 'Edit the chat message sent automatically when creators are hired',
      action: 'hireMessage',
      icon: 'hireMessage',
    },
    {
      id: 2,
      title: 'Private Brief',
      description: `We will hide your brief so you won't get any applicants. The only way to get applicants is by inviting them.`,
      action: 'pauseResume',
      icon: 'private',
    },
    {
      id: 3,
      title: 'Public Brief',
      description: `We will show your brief so all on SHOUT can apply.`,
      action: 'pauseResume',
      icon: 'public',
    },
    {
      id: 4,
      title: 'Cancel invites',
      description: `Un-invite all creators that are currently 'invited`,
      action: 'cancelInvites',
      icon: 'cancelInvites',
    },
    {
      id: 5,
      title: 'Decline All Applicants',
      description: `This will also send a notification saying 'Brief is now full, thank you for applying'`,
      action: 'declineApplicants',
      icon: 'declineAll',
    },
    {
      id: 6,
      title: 'Un-Hire All Applicants',
      description: `This will un-hire all creators who currently have the "Hired" status`,
      action: 'declineApplicants',
      icon: 'unhireAll',
    },
    {
      id: 7,
      title: 'Mark Complete',
      description: "This will decline the remaining applicants and cancel invites",
      action: 'declineApplicants',
      icon: 'complete',
    },
  ]
  /** Brief Settings Variables  - END */




  /** Services */
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  public manageBriefTabsService = inject(ManageBriefTabsService);
  protected readonly themeService = inject(ThemeService);
  currentUserService = inject(CurrentUserService);
  alertService = inject(AlertService);
  public briefService = inject(BriefService);
  public magicSearchService = inject(MagicSearchService);
  public centralManagement = inject(CentralManagementService);
  public choosePlanService = inject(ChoosePlanService);
  public toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  subscriptionList = new Subscription();

  constructor() {
    effect(() => {
      this.manageBriefTabsService.uiSelectedTab.set(this.selectedTab());
      this.manageBriefTabsService.uiSelectedSubTab.set(this.selectedSubTab());
    });
    effect(() => {
      // Depend on profile/screening filters + search/status so Apply/Clear/search refetches
      this.manageBriefTabsService.applicantProfileFilters();
      this.manageBriefTabsService.applicantScreeningFilters();
      this.manageBriefTabsService.applicantSort();
      this.manageBriefTabsService.needsYouFilter();
      this.magicSearchService.filtersObjectSignal();

      if (this.manageBriefTabsService.focussedBriefId) {
        this.manageBriefTabsService.refreshApplicants();
      }
    });

    effect(() => {
      const portfolioAction = this.centralManagement.portfolioAction();
      if (portfolioAction) {
        untracked(() => {
          const hiringFromOpenDrawer =
            portfolioAction.actionType === ActionTypesEnum.HIRE &&
            this.centralManagement.isItemisedHireOpen();
          this.manageBriefTabsService.actOnApplicationStatus(
            portfolioAction.applicant!,
            portfolioAction.actionType!,
          );
          this.centralManagement.portfolioAction.set(null);
          // A hire keeps its drawer open until the API answers: `hireCreator` closes it on
          // success, and a refused hire leaves it open to show why.
          if (!hiringFromOpenDrawer) {
            this.centralManagement.isItemisedHireOpen.set(false);
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.manageBriefTabsService.focussedBrief.set(undefined);
    this.manageBriefTabsService.clearApplicantProfileFilters();
    this.manageBriefTabsService.resetApplicantSort();
    this.manageBriefTabsService.needsYouFilter.set(false);
    this.manageBriefTabsService.assignmentSummary.set(null);
    this.briefService.focussedBriefSignal.set(undefined);

    this.subscriptionList.add(
      this.route.paramMap.subscribe((params) => this.applyRouteParams(params)),
    );
  }

  private applyRouteParams(params: ParamMap): void {
    const briefId = params.get('briefId');
    const tabParam = params.get('tab');
    const detailParam = params.get('subTab');
    const tab = tabFromManageBriefParam(tabParam);
    const slug = slugForManageBriefTab(tab);

    if (briefId) {
      const isNewBrief = briefId !== this.manageBriefTabsService.focussedBriefId;
      this.manageBriefTabsService.focussedBriefId = briefId;
      const briefMissingOrStale = this.manageBriefTabsService.focussedBrief()?.id !== briefId;
      if (isNewBrief || briefMissingOrStale) {
        this.getBrief();
        this.getFilterStats();
        this.manageBriefTabsService.getLatestVideos();
      }
    }

    if (briefId && tabParam !== slug) {
      const extra = creatorIdFromManageBriefDetail(detailParam)
        ? { creatorId: detailParam }
        : isManageBriefAssignmentSubTab(detailParam)
          ? { subTab: Number(detailParam) }
          : undefined;
      void this.router.navigate(manageBriefCommands(briefId, tab, extra), { replaceUrl: true });
      return;
    }

    this.selectedTab.set(tab);
    this.selectedSubTab.set(
      tab === BriefProgressEnum.Assignment && isManageBriefAssignmentSubTab(detailParam)
        ? Number(detailParam)
        : 0,
    );
    this.manageBriefTabsService.videoFeedbackCreatorId.set(creatorIdFromManageBriefDetail(detailParam));
  }

  manageBriefTabLink(tab: BriefProgressEnum): (string | number)[] {
    const briefId = this.manageBriefTabsService.focussedBriefId || this.route.snapshot.params['briefId'];
    return manageBriefCommands(briefId, tab);
  }

  selectTab(tab: BriefProgressEnum): void {
    this.manageBriefTabsService.navigateToBriefTab(tab);
  }

  refreshBrief() {

    console.log('refreshBrief() TABS - refreshing brief');

    this.getBrief();
    this.getFilterStats();
  }

  getBrief() {
    if (this.manageBriefTabsService.focussedBriefId) {
      const sub = this.briefService.getBriefById(this.manageBriefTabsService.focussedBriefId).subscribe({
        next: (briefRes: BriefResponseDto) => {
          this.manageBriefTabsService.focussedBrief.set(briefRes);
          this.approvedContentService.load(briefRes.id);
          this.briefService.focussedBriefSignal.set(briefRes);
          this.briefService.currentBriefSignal.set(briefRes);

          console.log('🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨 TABS BRIEF: ', briefRes);
          console.log('🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨 TABS - service - BRIEF: ', this.manageBriefTabsService.focussedBrief());
        },
        error: err => {
          console.error('error getting brief: ', err);
        }
      });
      this.subscriptionList.add(sub);
    }
  }



  getFilterStats() {
    if (this.manageBriefTabsService.focussedBriefId) {
      this.briefService.getFilterStats(this.manageBriefTabsService.focussedBriefId).subscribe(filterStats => {
        this.manageBriefTabsService.filterStats.set(filterStats);
        this.manageBriefTabsService.filterStats.set({
          ...filterStats,
          Completed: filterStats.Completed + filterStats.Posted + filterStats.Paid
        });

        this.manageBriefTabsService.statusFilterButtons.set(this.configureFilterButtons(filterStats));

        let allZero: boolean = false;
        Object.keys(filterStats).forEach(key => {
          if (filterStats[key] == 0) {
            allZero = true;
          }
        });
      })
    }
  }

  configureFilterButtons(filterStats: FilterStatsDto) {
    return [
      {
        applicationStatus: ApplicationStatusEnum.All,
        btnText: 'All',
        filterStats: this.manageBriefTabsService.filterStats().All
      },
      {
        applicationStatus: ApplicationStatusEnum.Invited,
        btnText: 'Invited',
        filterStats: this.manageBriefTabsService.filterStats().Invited
      },
      {
        applicationStatus: ApplicationStatusEnum.Applied,
        btnText: 'Applied',
        filterStats: this.manageBriefTabsService.filterStats().Applied
      },
      {
        applicationStatus: ApplicationStatusEnum.Hired,
        btnText: 'Hired',
        filterStats: this.manageBriefTabsService.filterStats().Hired
      },
      {
        applicationStatus: ApplicationStatusEnum.Started,
        btnText: 'Started',
        filterStats: this.manageBriefTabsService.filterStats().Started
      },
      {
        applicationStatus: ApplicationStatusEnum.ReadyToPost,
        btnText: 'Posting',
        filterStats: this.manageBriefTabsService.filterStats().ReadyToPost
      },
      {
        applicationStatus: ApplicationStatusEnum.Completed,
        btnText: 'Completed',
        filterStats: this.manageBriefTabsService.filterStats().Completed
      }
    ];
  }



  backToBriefs() {
    this.location.back();
  }

  selectSettingsOption(option: { id: number, title: string, description: string, action: string }) {
    console.log('selectSettingsOption(): ', option);
    switch (option.id) {
      case 1: // Edit Brief
        this.manageBriefTabsService.editBrief();
        break;
      case 2: // Private Brief
        this.manageBriefTabsService.pauseResume();
        break;
      case 3: // Public Brief
        this.manageBriefTabsService.pauseResume();
        break;
      case 4: // Cancel invites
        this.manageBriefTabsService.cancelInvites();
        break;
      case 5: // Decline All Applicants
        this.manageBriefTabsService.declineApplicants();
        break;
      case 6: // Un-Hire All Applicants
        this.manageBriefTabsService.removeAllHired();
        break;
      case 7: // Mark Complete
        this.markComplete();
        break;
      case 8: // Hire Message
        this.openHireMessageDialog();
        break;
      default:
        break;
    }
  }

  /** The Briefing tab's Hire Message card is the one place to edit it: go there and highlight it. */
  openHireMessageDialog(): void {
    this.manageBriefTabsService.briefingFocus.set('hire');
    this.manageBriefTabsService.navigateToBriefTab(BriefProgressEnum.Briefing);
  }

  async markComplete() {
    if (this.manageBriefTabsService.focussedBrief()) {
      const confirm = await this.alertService.confirm({
        title: 'Brief Complete?',
        message: `Is the brief: ${this.manageBriefTabsService.focussedBrief()?.title} finished? This means you've received all required deliverables and you want to close applications.`,
        confirmText: 'Mark Complete',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'info'
      });

      console.log('confirm: ', confirm);
      if (confirm) {
        this.briefService.completeBrief(this.manageBriefTabsService.focussedBrief()?.id!).pipe(take(1)).subscribe({
          next: briefPausedRes => {
            console.log('briefPausedRes: ', briefPausedRes);
            this.manageBriefTabsService.focussedBrief.set(briefPausedRes);
            this.toastNotificationService.alert({
              title: 'Brief Marked Complete!',
              message: 'The brief has been marked complete. Creators will no longer be able to apply.',
              type: 'success',
              icon: 'success'
            });
          },
          error: err => {
            console.error('briefPausedRes ERROR: ', err);
            this.toastNotificationService.alert({
              title: 'Error Marking Brief Complete!',
              message: 'An error occurred while marking the brief complete.',
              type: 'error',
              icon: 'error'
            });
          }
        });
      }
    }
  }



  showSettingsOption(option: { id: number, title: string, description: string, action: string }) {
    switch (option.id) {
      case 1: // Edit Brief
        return true;
      case 2: // Private Brief
        return this.manageBriefTabsService.focussedBrief()?.currentStatus?.status === 0;
      case 3: // Public Brief
        return this.manageBriefTabsService.focussedBrief()?.currentStatus?.status === 1;
      case 4: // Cancel invites
        return true;
      case 5: // Decline All Applicants
        return true;
      case 6: // Un-Hire All Applicants
        if (this.currentUserService.hasAnyRole([4])) {
          return true;
        }
        return false;
      case 7: // Mark Complete
        return true;
      case 8: // Hire Message
        return true;
      default:
        return false;
    }
  }

  selectTabMobile(event: any) {
    console.log('selectTabMobile()', event);
    this.selectTab(Number(event.target.value) as BriefProgressEnum);
  }

  getTabClass(tab: BriefProgressEnum) {
    return this.selectedTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  getBadgeColour() {
    const ask = summariseBriefAsk(this.briefService.focussedBriefSignal() ?? {});
    if (ask.source === 'pieces') {
      return 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/20';
    }
    if (this.briefService.focussedBriefSignal() && this.briefService.focussedBriefSignal()?.currentPrice && this.briefService.focussedBriefSignal()?.currentPrice?.ugcPrice && this.briefService.focussedBriefSignal()?.currentPrice?.ugcPrice > 0) {
      return 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/20';
    } else if (briefCarriesAdLicense(this.briefService.focussedBriefSignal())) {
      return 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 ring-teal-600 ring-opacity-20';
    } else {
      switch (this.briefService.focussedBriefSignal()?.shoutBrief?.socialPlatform) {
        case SocialChannelEnum['TikTok Post']:
          return 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 ring-orange-600 ring-opacity-20';
        case SocialChannelEnum['Instagram Reel']:
          return 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 ring-sky-600 ring-opacity-20';
        case SocialChannelEnum['Instagram Story']:
          return 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/20';
        case SocialChannelEnum['YouTube Shorts']:
          return 'bg-red-50 dark:bg-red-900/30 text-red-700 ring-red-600 ring-opacity-20';
        case SocialChannelEnum['Snapchat Spotlight']:
          return 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 ring-yellow-600 ring-opacity-20';
        case SocialChannelEnum['Snapchat Story']:
          return 'bg-gold-50 dark:bg-gold-900/30 text-gold-700 ring-gold-600 ring-opacity-20';
        default:
          return 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/20';
      }
    }
  }

  getPostType() {
    const ask = summariseBriefAsk(this.briefService.focussedBriefSignal() ?? {});
    if (ask.source === 'pieces') {
      return ask.label;
    }
    if (this.briefService.focussedBriefSignal() && this.briefService.focussedBriefSignal()!.currentPrice && this.briefService.focussedBriefSignal()!.currentPrice?.ugcPrice && this.briefService.focussedBriefSignal()!.currentPrice?.ugcPrice > 0) {
      return 'UGC Only';
    } else if (briefCarriesAdLicense(this.briefService.focussedBriefSignal())) {
      return 'Ad Content';
    } else if (!this.briefService.focussedBriefSignal()?.shoutBrief) {
      // No social posting: UGC (SHOUT+ voucher/product briefs have £0 prices, so the checks above miss them).
      return 'UGC Only';
    } else {
      switch (this.briefService.focussedBriefSignal()?.shoutBrief?.socialPlatform) {
        case SocialChannelEnum['TikTok Post']:
          return 'TikTok Post';
        case SocialChannelEnum['Instagram Reel']:
          return 'Instagram Reel';
        case SocialChannelEnum['Instagram Story']:
          return 'Instagram Story';
        case SocialChannelEnum['YouTube Shorts']:
          return 'YouTube Shorts';
        case SocialChannelEnum['Snapchat Spotlight']:
          return 'Snapchat Spotlight';
        case SocialChannelEnum['Snapchat Story']:
          return 'Snapchat Story';
        default:
          return 'Social Post';
      }
    }
  }

  showDeliveryAddress(): boolean {
    if (this.manageBriefTabsService.focussedBrief()?.deliveryType == 2) {
      return true;
    }
    return false;
  }

  showsReportingTab(): boolean {
    return briefShowsReportingTab(this.briefService.focussedBriefSignal());
  }

  showsTikTokAdsTabs(): boolean {
    return briefShowsTikTokAdsTabs(this.briefService.focussedBriefSignal());
  }

  isDeliveryBrief() {
    if (this.manageBriefTabsService.focussedBrief() && this.manageBriefTabsService.focussedBrief()!.deliveryType == 2) {
      return true;
    }
    return false;
  }

  goToTab(event: { tab: number, subTab: number }) {
    console.log('goToTab()', event);
    this.manageBriefTabsService.navigateToBriefTab(event.tab as BriefProgressEnum, {
      subTab: event.tab === BriefProgressEnum.Assignment ? event.subTab : undefined,
    });
  }

  onGoToLogistics(applicationId: string): void {
    this.manageBriefTabsService.logisticsHighlightApplicationId.set(applicationId);
    this.manageBriefTabsService.navigateToBriefTab(BriefProgressEnum.Logistics);
  }

  async alertAutoPilotOff() {
    const confirm = await this.alertService.confirm({
      title: 'Diasble Auto Pilot?',
      message: 'Are you sure you want to turn off auto pilot?',
      confirmText: 'Turn Off',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'info'
    });
    if (confirm) {
      this.briefService.toggleAutoPilot(this.manageBriefTabsService.focussedBriefId).subscribe({
        next: (response) => {
          console.log('response: ', response);
        }
      });
    }
  }

  getAutoPilotEnabled() {
    if (this.manageBriefTabsService.focussedBrief()?.enableAutoPilot) {
      return true;
    }
    return false;
  }

  launchOverallBriefResults(): void {
    void this.router.navigate(['/manage-results', this.manageBriefTabsService.focussedBriefId]);
  }

  launchSuperResults(): void {
    this.manageBriefTabsService.navigateToBriefTab(BriefProgressEnum.Reporting);
  }

  launchShoutPlusBriefResults(): void {
    void this.router.navigate(['/shout-plus/brief-results', this.manageBriefTabsService.focussedBriefId]);
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}
